// EventSearchPalette.js — recherche d'événements en ligne (⌘K / Ctrl+K)
// Interroge GET /events/search dès 2 caractères et affine à chaque frappe.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, CalendarDays, MapPin, User, X, CornerDownLeft } from 'lucide-react';
import { quickSearchEvents } from '../api/api';
import { safeFormatDate } from '../utils/dateUtils';
import './EventSearchPalette.css';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 180;

// Repli accent → lettre de base en préservant la longueur (1 char = 1 char),
// pour que les index calculés sur la version normalisée restent valides sur
// la chaîne d'origine et permettent de surligner la portion trouvée.
const foldPreservingLength = (str) => Array.from(str)
  .map((ch) => {
    const base = ch.normalize('NFD')[0];
    return base || ch;
  })
  .join('')
  .toLowerCase();

/**
 * Découpe un libellé en segments {text, match} pour surligner les portions
 * correspondant aux mots saisis (insensible casse/accents).
 */
const highlightSegments = (label, tokens) => {
  if (!label || tokens.length === 0) return [{ text: label, match: false }];

  const folded = foldPreservingLength(label);
  const ranges = [];

  tokens.forEach((token) => {
    const needle = foldPreservingLength(token);
    if (!needle) return;
    let from = 0;
    let at = folded.indexOf(needle, from);
    while (at !== -1) {
      ranges.push([at, at + needle.length]);
      from = at + needle.length;
      at = folded.indexOf(needle, from);
    }
  });

  if (ranges.length === 0) return [{ text: label, match: false }];

  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  for (const [start, end] of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }

  const segments = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) segments.push({ text: label.slice(cursor, start), match: false });
    segments.push({ text: label.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < label.length) segments.push({ text: label.slice(cursor), match: false });
  return segments;
};

const formatEventDate = (date) => {
  if (!date?.start) return null;
  const start = safeFormatDate(date.start, 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }, null);
  if (!start) return null;
  const end = date.end && date.end !== date.start
    ? safeFormatDate(date.end, 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }, null)
    : null;
  return end && end !== start ? `${start} → ${end}` : start;
};

const EventSearchPalette = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH;
  const tokens = useMemo(() => trimmed.split(/\s+/).filter(Boolean), [trimmed]);

  // Réinitialiser à chaque ouverture
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
    setError(null);
    setActiveIndex(0);
    const focus = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(focus);
  }, [open]);

  // Recherche debouncée + annulation de la requête précédente
  useEffect(() => {
    if (!open) return undefined;

    if (trimmed.length < MIN_QUERY_LENGTH) {
      abortRef.current?.abort();
      setResults([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const found = await quickSearchEvents(trimmed, { limit: 12, signal: controller.signal });
        if (controller.signal.aborted) return;
        setResults(found);
        setActiveIndex(0);
        setError(null);
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        console.error('[EventSearchPalette] Erreur de recherche:', err);
        setError(t('search.error', 'La recherche a échoué. Réessayez.'));
        setResults([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmed, open, t]);

  // Annuler la requête en vol au démontage
  useEffect(() => () => abortRef.current?.abort(), []);

  const goToEvent = useCallback((event) => {
    if (!event) return;
    onClose();
    navigate(`/events/${event.id}`);
  }, [navigate, onClose]);

  const goToAllResults = useCallback(() => {
    onClose();
    navigate(`/evenements?search=${encodeURIComponent(trimmed)}`);
  }, [navigate, onClose, trimmed]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (results.length ? (prev + 1) % results.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) goToEvent(results[activeIndex]);
      else if (trimmed.length >= MIN_QUERY_LENGTH) goToAllResults();
    }
  };

  // Garder l'élément actif visible pendant la navigation clavier
  useEffect(() => {
    const node = listRef.current?.querySelector('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, results]);

  if (!open) return null;

  // Portal sur <body> : la sidebar est en overflow:hidden et pourrait rogner la palette.
  // Le fond ne se ferme que si le clic le vise lui-même : un stopPropagation sur le
  // panneau donnerait le même résultat, mais attacherait un gestionnaire de souris à
  // un élément non interactif (role="dialog"), ce que jsx-a11y refuse à juste titre.
  return createPortal(
    <div
      className="event-search-overlay"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="event-search-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.title', 'Rechercher un événement')}
      >
        <div className="event-search-input-row">
          {loading ? <Loader2 size={17} className="event-search-spinner" /> : <Search size={17} />}
          <input
            ref={inputRef}
            className="event-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('search.placeholder', 'Nom d\'événement, client, lieu, chef de projet…')}
            autoComplete="off"
            spellCheck="false"
          />
          <button type="button" className="event-search-close" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
            <X size={15} />
          </button>
        </div>

        <div className="event-search-body" ref={listRef}>
          {trimmed.length === 0 && (
            <p className="event-search-hint">
              {t('search.hintStart', 'Tapez au moins 2 caractères pour lancer la recherche.')}
            </p>
          )}

          {tooShort && (
            <p className="event-search-hint">
              {t('search.hintMore', 'Encore un caractère…')}
            </p>
          )}

          {error && <p className="event-search-error">{error}</p>}

          {!error && !loading && trimmed.length >= MIN_QUERY_LENGTH && results.length === 0 && (
            <p className="event-search-hint">
              {t('search.noResults', 'Aucun événement trouvé pour « {{query}} »', { query: trimmed })}
            </p>
          )}

          {results.map((event, index) => {
            const dateLabel = formatEventDate(event.date);
            return (
              <button
                type="button"
                key={event.id}
                data-active={index === activeIndex}
                className={`event-search-result${index === activeIndex ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => goToEvent(event)}
              >
                <span className="event-search-result-main">
                  <span className="event-search-result-name">
                    {highlightSegments(event.nom || t('search.untitled', 'Sans nom'), tokens).map((seg, i) => (
                      seg.match
                        ? <mark key={i}>{seg.text}</mark>
                        : <span key={i}>{seg.text}</span>
                    ))}
                  </span>
                  <span className="event-search-result-meta">
                    {dateLabel && <span><CalendarDays size={12} />{dateLabel}</span>}
                    {event.lieu && <span><MapPin size={12} />{event.lieu}</span>}
                    {event.client && <span><User size={12} />{event.client}</span>}
                  </span>
                </span>
                {event.statut && <span className="event-search-result-status">{event.statut}</span>}
              </button>
            );
          })}

          {results.length > 0 && (
            <button type="button" className="event-search-all" onClick={goToAllResults}>
              {t('search.seeAll', 'Voir tous les événements correspondants')}
            </button>
          )}
        </div>

        <div className="event-search-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> {t('search.navigate', 'naviguer')}</span>
          <span><kbd><CornerDownLeft size={10} /></kbd> {t('search.open', 'ouvrir')}</span>
          <span><kbd>Esc</kbd> {t('search.close', 'fermer')}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventSearchPalette;
